import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { Button } from "@common/button";
import {
  Identifiable,
  Project,
  projectConfiguration,
  ProjectConfiguration,
  ProjectPortfolio,
} from "@planzone/dto";
import { project as projectMock } from "@planzone/dto";
import {
  useProjectCreation,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectDetailsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import { useModal } from "@root/client/modal";
import { Modal } from "@common/modal";
import { ProjectCreationForm } from "../projectCreationForm";
import { Props } from "./types";
import { ProjectTypeChoice } from "./projectTypeChoice";
import styles from "./styles.scss";

function useRequiredFields(project: Project) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const { configuration } = useRetrieveProjectDetailsConfiguration(planzone);
  const roles = useRoles({ project });
  const profile =
    configuration.find((p) =>
      p.roles.find((role) =>
        roles.find((r) => r.uuid === role || r.name === role),
      ),
    ) || configuration.length > 0
      ? configuration[0]
      : {
          name: "",
          roles: [],
          elements: [],
        };
  const fields = template.filter(
    ({ uuid, attr }) =>
      !!profile.elements.find(({ key }) => key === uuid) &&
      attr.required === "true",
  );

  return !!fields.find(
    ({ uuid }) =>
      !project.attributes[uuid] || project.attributes[uuid].length === 0,
  );
}

function ProjectCreation({ zone, isTemplate, isMultiProject, close }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { isRequestPending, createProject } = useProjectCreation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const [isPrintingChoices, setIsPrintingChoices] = React.useState(true);
  const [selectedZone, setSelectedZone] = useState(zone || null);
  const [project, setProject] = React.useState<
    Project & { portfolios: Identifiable<ProjectPortfolio>[] }
  >({
    ...projectMock,
    isTemplate,
    portfolios: [],
  });
  const [configuration, setConfiguration] =
    useState<ProjectConfiguration>(projectConfiguration);
  const hasEmptyRequiredFields = useRequiredFields(project);

  return isPrintingChoices && !isTemplate ? (
    <ProjectTypeChoice
      createProject={() => setIsPrintingChoices(false)}
      close={close}
      useModel={() => {
        close();
        pushView({
          key: UseCases.useProjectModel,
          data: { zone },
        });
      }}
    />
  ) : (
    <Modal
      header={
        !isTemplate
          ? t("projectCreation.title")
          : t("projectCreation.projectModelTitle")
      }
      close={close}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          selectedZone &&
            createProject({
              zone: selectedZone,
              project,
              configuration,
              portfolios: project.portfolios,
              planzone,
            });
        }}
        className={styles.form}
      >
        <ProjectCreationForm
          zone={selectedZone}
          isMultiProject={isMultiProject}
          selectZone={setSelectedZone}
          formData={project}
          configuration={configuration}
          editForm={setProject}
          setConfiguration={setConfiguration}
        />
        <div className={styles.buttons}>
          <Button type="cancel" press={close}>
            {t("projectCreation.cancel")}
          </Button>
          <Button
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={!selectedZone || hasEmptyRequiredFields}
          >
            {t("projectCreation.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectCreation };
